/**
 * BANDEJA DE ESCANEOS PENDIENTES (F13.3) — /inventario/escaneos-pendientes
 *
 * Códigos leídos que no identifican nada en NeatOS (nunca se crea un artículo
 * al escanear). ADMIN y CONSULTOR los asignan como código de un artículo
 * —con las reglas de F13.1: GTIN validado, únicos— o los descartan con una
 * nota. Una fila por valor, con cuántas veces se ha leído.
 */
import { useState, type ReactNode } from 'react';

import { api, type OpcionArticuloStock, type TipoCodigoArticulo } from '../api.ts';
import { Error as ErrorCaja, fechaHora, Modal, Panel, useDatos, Vacio } from '../componentes/base.tsx';
import { Buscador } from '../componentes/buscador.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { nombreArticulo } from './stock.tsx';
import { codigoAlmacen } from '../componentes/codigo-articulo.tsx';

export interface EscaneoPendiente {
  id: string; valor: string; tipoDetectado: string | null; estado: 'PENDIENTE' | 'RESUELTO' | 'DESCARTADO'; repeticiones: number;
  primeraVez: string; ultimaVez: string; ultimoUsuario: string; almacen: { id: string; codigo: string } | null;
  articulo: { id: string; codigo: string; descripcion: string } | null; nota: string | null;
}

const TIPOS: readonly (readonly [TipoCodigoArticulo, string])[] = [
  ['EAN13', 'EAN-13'], ['EAN8', 'EAN-8'], ['UPCA', 'UPC-A'], ['GTIN14', 'GTIN-14'], ['CODE128', 'Code 128'], ['CODE39', 'Code 39'],
  ['QR', 'QR'], ['DATAMATRIX', 'Data Matrix'], ['OTRO', 'Otro'],
];
const gtinDe = (v: string): string | null => /^\(01\)(\d{14})/.exec(v)?.[1] ?? null;

export function PaginaEscaneosPendientes(): ReactNode {
  const { puede } = useSesion();
  const [estado, setEstado] = useState('PENDIENTE');
  const lista = useDatos(() => api.get<{ filas: EscaneoPendiente[]; total: number }>(`/api/inventario/escaneos-pendientes?estado=${estado}&porPagina=100`), [estado]);
  const [asignar, setAsignar] = useState<EscaneoPendiente | null>(null);
  const [descartar, setDescartar] = useState<EscaneoPendiente | null>(null);
  return (
    <Shell dominio="articulos" migas={[{ texto: 'Inventario' }, { texto: 'Artículos', a: '/inventario/articulos' }, { texto: 'Escaneos pendientes' }]} titulo="Escaneos pendientes"
      subtitulo="Códigos leídos que no identifican nada en NeatOS. Asígnalos como código de un artículo o descártalos: escanear nunca crea artículos.">
      <div className="panel">
        <div className="panel-cuerpo">
          <label className="campo" style={{ maxWidth: 260 }}><span>Mostrar</span>
            <select value={estado} onChange={(ev) => setEstado(ev.target.value)}>
              <option value="PENDIENTE">Pendientes</option><option value="RESUELTO">Asignados</option><option value="DESCARTADO">Descartados</option><option value="TODOS">Todos</option>
            </select>
          </label>
        </div>
        <Panel estado={lista}>
          {(d) => (d.filas.length === 0 ? <Vacio titulo={estado === 'PENDIENTE' ? 'No hay códigos pendientes' : 'Nada que mostrar'} /> : (
            <div className="tabla-desliza">
              <table aria-label="Escaneos pendientes" className="tabla-tarjetas">
                <thead><tr><th>Código leído</th><th className="num">Lecturas</th><th>Última vez</th><th>Almacén</th><th>Estado</th><th aria-label="Acciones" /></tr></thead>
                <tbody>
                  {d.filas.map((p) => (
                    <tr key={p.id}>
                      <td className="celda-titulo mono" style={{ overflowWrap: 'anywhere' }}>{p.valor}{p.tipoDetectado !== null && <span className="suave pequena"> · {p.tipoDetectado}</span>}</td>
                      <td className="num" data-label="Lecturas">{p.repeticiones}</td>
                      <td data-label="Última vez" className="pequena">{fechaHora(p.ultimaVez)} · {p.ultimoUsuario}</td>
                      <td data-label="Almacén">{p.almacen === null ? '—' : codigoAlmacen(p.almacen)}</td>
                      <td data-label="Estado">{p.estado === 'PENDIENTE' ? <span className="etiqueta aviso">Pendiente</span>
                        : p.estado === 'RESUELTO' ? <span className="etiqueta exito">Asignado a {p.articulo?.codigo}</span>
                          : <span className="etiqueta neutra" title={p.nota ?? undefined}>Descartado</span>}</td>
                      <td data-label="Acciones">{p.estado === 'PENDIENTE' && (
                        <div className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
                          {puede('articulos:codigos') && <button type="button" onClick={() => setAsignar(p)} aria-label={`Asignar ${p.valor}`}>Asignar a un artículo</button>}
                          <button type="button" onClick={() => setDescartar(p)} aria-label={`Descartar ${p.valor}`}>Descartar</button>
                        </div>
                      )}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Panel>
      </div>
      {asignar !== null && <DialogoAsignar p={asignar} cerrar={() => setAsignar(null)} alHacer={() => { setAsignar(null); lista.recargar(); }} />}
      {descartar !== null && <DialogoDescartar p={descartar} cerrar={() => setDescartar(null)} alHacer={() => { setDescartar(null); lista.recargar(); }} />}
    </Shell>
  );
}

function DialogoAsignar({ p, cerrar, alHacer }: { p: EscaneoPendiente; cerrar: () => void; alHacer: () => void }): ReactNode {
  const gtin = gtinDe(p.valor);
  const [articulo, setArticulo] = useState<OpcionArticuloStock | null>(null);
  //  F13.3.1: '' = automático (contenido + simbología que informó el lector). El tipo técnico sólo para ADMIN.
  const [tipo, setTipo] = useState<TipoCodigoArticulo | ''>('');
  const { usuario } = useSesion();
  const esAdmin = usuario?.roles.some((r) => r.rol === 'ADMIN') === true;
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  async function hacer(): Promise<void> {
    if (articulo === null) return;
    setError(null);
    setEnviando(true);
    try {
      await api.post(`/api/inventario/escaneos-pendientes/${p.id}/asignar`, { idArticulo: articulo.id, ...(tipo === '' ? {} : { tipo }) });
      alHacer();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }
  return (
    <Modal titulo={`Asignar ${p.valor}`} cerrar={cerrar}
      pie={<><button type="button" onClick={cerrar}>Cancelar</button>
        <button type="button" className="principal" disabled={enviando || articulo === null || gtin !== null} onClick={() => void hacer()}>Asignar</button></>}>
      {error !== null && <ErrorCaja error={error} />}
      {gtin !== null ? (
        <p className="nota aviso">Es una etiqueta GS1 compuesta: lo que identifica al artículo es su GTIN <span className="mono">{gtin}</span>. Añádelo en «Códigos y ERP» del artículo y descarta este pendiente.</p>
      ) : (
        <>
          <p className="pequena">El código quedará registrado en el artículo y, desde ese momento, cualquier lectura lo identificará.</p>
          <Buscador<OpcionArticuloStock> etiqueta="Artículo" valor={articulo} alElegir={setArticulo} clave={(a) => a.id} texto={nombreArticulo}
            buscar={async (t) => (await api.get<{ filas: OpcionArticuloStock[] }>(`/api/inventario/opciones/articulos?${new URLSearchParams({ texto: t, limite: '30' }).toString()}`)).filas}
            sinResultados="Ningún artículo activo." />
          {esAdmin && (
            <details>
              <summary className="pequena">Opciones técnicas</summary>
              <div className="campo"><label htmlFor="ep-tipo">Tipo técnico del código</label>
                <select id="ep-tipo" value={tipo} onChange={(ev) => setTipo(ev.target.value as TipoCodigoArticulo | '')}>
                  <option value="">Automático (recomendado)</option>
                  {TIPOS.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
                </select></div>
            </details>
          )}
        </>
      )}
    </Modal>
  );
}

function DialogoDescartar({ p, cerrar, alHacer }: { p: EscaneoPendiente; cerrar: () => void; alHacer: () => void }): ReactNode {
  const [nota, setNota] = useState('');
  const [error, setError] = useState<unknown>(null);
  async function hacer(): Promise<void> {
    setError(null);
    try {
      await api.post(`/api/inventario/escaneos-pendientes/${p.id}/descartar`, { nota: nota.trim() });
      alHacer();
    } catch (e) {
      setError(e);
    }
  }
  return (
    <Modal titulo={`Descartar ${p.valor}`} cerrar={cerrar}
      pie={<><button type="button" onClick={cerrar}>Cancelar</button>
        <button type="button" className="peligro" disabled={nota.trim().length < 3} onClick={() => void hacer()}>Descartar</button></>}>
      {error !== null && <ErrorCaja error={error} />}
      <div className="campo"><label htmlFor="ep-nota">Motivo</label>
        <textarea id="ep-nota" rows={2} maxLength={500} value={nota} onChange={(ev) => setNota(ev.target.value)} placeholder="Por ejemplo: etiqueta de un proveedor, no es un producto" /></div>
    </Modal>
  );
}
